import React, { useEffect, useMemo, useState } from 'react';
import { FaComments } from 'react-icons/fa';
import Modal from './Modal';
import RecentActivityList from '../projects/RecentActivityList';
import { useApi } from '../state/ApiContext';
import { useUserContext } from '../state/UserContext';
import { useChatDock } from '../state/ChatDockContext';
import type { ApiTeamTeamIdUsersGet200ResponseInner } from '../client';
import '../member-details.css';

interface MemberDetailModalProps {
  teamId: string;
  userId: number;
  onClose: () => void;
}

/** Display name matching the chat composer's @mention autocomplete */
export function memberDisplayName(member: {
  firstName?: string;
  lastName?: string;
  email?: string;
  id?: number;
}): string {
  return (
    `${member.firstName || ''} ${member.lastName || ''}`.trim() ||
    member.email ||
    `User ${member.id}`
  );
}

/**
 * Profile + activity for one team member, opened from any member name
 * via MemberDetailProvider. Offers "Message" (a fresh chat conversation
 * pre-addressed to them) when the member isn't the viewer.
 */
const MemberDetailModal: React.FC<MemberDetailModalProps> = ({
  teamId,
  userId,
  onClose,
}) => {
  const { teamApi } = useApi();
  const { user: currentUser } = useUserContext();
  const { startMemberChat } = useChatDock();
  const [member, setMember] =
    useState<ApiTeamTeamIdUsersGet200ResponseInner | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMember(null);
    setError(null);
    teamApi
      .apiTeamTeamIdUsersGet(Number(teamId))
      .then((response) => {
        if (cancelled) return;
        const found = (response.data || []).find(
          (candidate) => candidate.id === userId,
        );
        if (found) {
          setMember(found);
        } else {
          setError('This person is no longer a member of the team.');
        }
      })
      .catch((requestError) => {
        console.error('Error loading member details:', requestError);
        if (!cancelled) setError('Could not load member details.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [teamApi, teamId, userId]);

  const name = useMemo(
    () => (member ? memberDisplayName(member) : ''),
    [member],
  );
  const isCurrentUser = currentUser?.id === userId;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={loading ? 'Member' : name || 'Member'}
      maxWidth="640px">
      <div data-testid="member-detail-modal">
        {loading ? (
          <p className="descriptionText">Loading member…</p>
        ) : error ? (
          <div className="projects-error">{error}</div>
        ) : member ? (
          <>
            <div className="member-detail-header">
              <div className="member-detail-info">
                <p className="descriptionText" style={{ margin: 0 }}>
                  <a href={`mailto:${member.email}`}>{member.email}</a>
                </p>
                {member.createdAt && (
                  <p className="descriptionText" style={{ margin: 0 }}>
                    Member since{' '}
                    {new Date(member.createdAt).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                )}
              </div>
              {!isCurrentUser && (
                <button
                  className="primaryButton member-detail-message"
                  data-testid="member-detail-message"
                  title={`Start a chat with ${name}`}
                  onClick={() => {
                    startMemberChat(teamId, name);
                    onClose();
                  }}>
                  <FaComments size={14} /> Message
                </button>
              )}
            </div>
            <h3 className="member-detail-activity-heading">Activity</h3>
            <RecentActivityList
              teamId={teamId}
              actorUserId={userId}
              pageSize={10}
              emptyText="No activity from this member yet."
              testId="member-activity"
              onNavigate={onClose}
            />
          </>
        ) : null}
      </div>
    </Modal>
  );
};

export default MemberDetailModal;
